import {
  ApiError,
  factoriesControllerArchiveFactory,
  factoriesControllerArchiveStation,
  factoriesControllerArchiveZone,
  factoriesControllerCreateFactory,
  factoriesControllerCreateStation,
  factoriesControllerCreateZone,
  fetchBlob,
  factoriesControllerGetZones,
  factoriesControllerListFactories,
  factoriesControllerRenameZone,
  factoriesControllerReorderStations,
  factoriesControllerReorderZones,
  factoriesControllerUnarchiveFactory,
  factoriesControllerUnarchiveStation,
  factoriesControllerUnarchiveZone,
  factoriesControllerUpdateFactory,
  factoriesControllerUpdateFactoryQualitySettings,
  factoriesControllerUpdateStation,
  getFactoriesControllerListFactoriesQueryKey,
  getFactoriesControllerDownloadStationScanLabelUrl,
  useCurrentUser,
  type FactoryListItemDto,
  type FactoryQualitySettingsDto,
  type UpdateFactoryQualitySettingsDto,
} from '@artic/api-client';
import { keepPreviousData, queryOptions, useMutation, useQueryClient } from '@tanstack/react-query';

import { expectData } from '../_shared/api';
import { ROUTES_KEY } from '../routing/queries';

export const FACTORY_ADMIN_KEY = 'factory-admin' as const;

// ── Response shapes (mirror the nest-backend factory-admin DTOs) ──────────────

/**
 * Partner-scoped factories list item, aliased directly from the generated DTO
 * rather than hand-duplicated: FactoryListItemDto is the one factory-admin
 * response shape the backend extended with the Quality Dashboard threshold
 * minutes (ENG-842) — the admin/topology shapes below (Factory, FactoryTopology)
 * were deliberately not, so this is also where EditFactorySheet reads current
 * threshold values from.
 */
export type FactoryListItem = FactoryListItemDto;

export interface StationNode {
  station_id: string;
  station_name: string;
  is_archived: boolean;
  /** Stored position within the zone (1..N); what the list is sorted by. */
  station_sequence: number;
}

export interface ZoneNode {
  zone_id: string;
  zone_name: string | null;
  is_archived: boolean;
  /** Stored position within the factory (1..N). */
  zone_sequence: number;
  stations: StationNode[];
}

export interface FactoryTopology {
  factory_id: string;
  factory_name: string | null;
  factory_timezone: string;
  is_archived: boolean;
  zones: ZoneNode[];
}

export interface Factory {
  factory_id: string;
  factory_name: string | null;
  factory_timezone: string;
  is_archived: boolean;
}

// ── Admin gate (factory create/edit/archive is Artic-admin only) ──────────────

/** Mirrors routes/admin/route.tsx + nest-backend isAdminUser (exact domain). */
export function useIsArticAdmin(): boolean {
  const { user } = useCurrentUser();
  const domain = user.email.toLowerCase().split('@').at(-1);
  return domain === 'artic.works' || domain === 'trunk.works';
}

// ── Conflict surface (409 → field-pinned error) ───────────────────────────────

export type ConflictField = 'factory_name' | 'zone_name' | 'station_name';

export class FactoryAdminConflictError extends Error {
  public readonly field: ConflictField;

  constructor(field: ConflictField, message: string) {
    super(message);
    this.name = 'FactoryAdminConflictError';
    this.field = field;
  }
}

function mapConflict(err: unknown): never {
  if (err instanceof ApiError && err.status === 409) {
    const body = err.bodyAsRecord;
    const rawField = body?.['field'];
    const rawMessage = body?.['message'];
    const message = typeof rawMessage === 'string' ? rawMessage : err.message;
    if (rawField === 'factory_name' || rawField === 'zone_name' || rawField === 'station_name') {
      throw new FactoryAdminConflictError(rawField, message);
    }
  }
  throw err;
}

// ── Queries ───────────────────────────────────────────────────────────────────

export function factoriesListQueryOptions(partnerId: string | undefined) {
  return queryOptions({
    queryKey: [FACTORY_ADMIN_KEY, 'list', partnerId] as const,
    queryFn: async ({ signal }) => {
      if (!partnerId) throw new Error('partnerId is required');
      const res = await factoriesControllerListFactories({ partner_id: partnerId }, { signal });
      return expectData<FactoryListItem[]>(res);
    },
    enabled: !!partnerId,
    staleTime: 30_000,
    placeholderData: keepPreviousData,
  });
}

export function factoryTopologyQueryOptions(factoryId: string | undefined) {
  return queryOptions({
    queryKey: [FACTORY_ADMIN_KEY, 'topology', factoryId] as const,
    queryFn: async ({ signal }) => {
      if (!factoryId) throw new Error('factoryId is required');
      const res = await factoriesControllerGetZones(factoryId, { signal });
      return expectData<FactoryTopology>(res);
    },
    enabled: !!factoryId,
    staleTime: 15_000,
    placeholderData: keepPreviousData,
  });
}

// ── Mutations ──────────────────────────────────────────────────────────────────
//
// Each mutation invalidates the affected factory's topology and the partner's
// factory list. The view records the touched id in a "recently edited" window so
// the realtime echo of the user's own change isn't re-animated (see
// use-factory-admin-channel.ts).

function useInvalidate() {
  const queryClient = useQueryClient();
  return {
    list: (partnerId: string | undefined) => {
      void queryClient.invalidateQueries({ queryKey: [FACTORY_ADMIN_KEY, 'list', partnerId] });
      // Also invalidate the generated hook's own query key: any other surface
      // that reads GET /factories straight through the generated Orval hook
      // (bypassing this feature's hand-rolled wrapper above) must see the same
      // fresh data — e.g. the Quality Dashboard threshold minutes this sheet
      // just wrote (ENG-842).
      void queryClient.invalidateQueries({
        queryKey: getFactoriesControllerListFactoriesQueryKey(
          partnerId ? { partner_id: partnerId } : undefined,
        ),
      });
    },
    topology: (factoryId: string) =>
      void queryClient.invalidateQueries({ queryKey: [FACTORY_ADMIN_KEY, 'topology', factoryId] }),
    routes: () => void queryClient.invalidateQueries({ queryKey: [ROUTES_KEY] }),
  };
}

export function useCreateFactory(partnerId: string | undefined) {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: async (input: { factory_name: string; factory_timezone: string }) => {
      if (!partnerId) throw new Error('partnerId is required');
      try {
        const res = await factoriesControllerCreateFactory({
          partner_id: partnerId,
          ...input,
        });
        return expectData<Factory>(res);
      } catch (err) {
        mapConflict(err);
      }
    },
    onSuccess: () => {
      invalidate.list(partnerId);
      invalidate.routes();
    },
  });
}

export function useUpdateFactory(partnerId: string | undefined, factoryId: string) {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: async (input: { factory_name?: string; factory_timezone?: string }) => {
      try {
        const res = await factoriesControllerUpdateFactory(factoryId, input);
        return expectData<Factory>(res);
      } catch (err) {
        mapConflict(err);
      }
    },
    onSuccess: () => {
      invalidate.list(partnerId);
      invalidate.topology(factoryId);
    },
  });
}

/**
 * Updates the Quality Dashboard thresholds for a factory (ENG-842).
 * Partner-scoped (not Artic-admin only) — see FactoryAccessGuard on the
 * backend route. Request/response types come straight from the generated
 * client (UpdateFactoryQualitySettingsDto / FactoryQualitySettingsDto)
 * rather than a hand-rolled interface, since this mutation has no other
 * consumer to keep in sync by hand.
 */
export function useUpdateFactoryQualitySettings(partnerId: string | undefined, factoryId: string) {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: async (input: UpdateFactoryQualitySettingsDto) => {
      const res = await factoriesControllerUpdateFactoryQualitySettings(factoryId, input);
      return expectData<FactoryQualitySettingsDto>(res);
    },
    onSuccess: () => invalidate.list(partnerId),
  });
}

export function useSetFactoryArchived(partnerId: string | undefined, factoryId: string) {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: async (archived: boolean) => {
      if (archived) {
        await factoriesControllerArchiveFactory(factoryId);
      } else {
        await factoriesControllerUnarchiveFactory(factoryId);
      }
    },
    onSuccess: () => invalidate.list(partnerId),
  });
}

export function useCreateZone(factoryId: string) {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: async (input: { zone_name: string }) => {
      try {
        const res = await factoriesControllerCreateZone(factoryId, input);
        return expectData<ZoneNode>(res);
      } catch (err) {
        mapConflict(err);
      }
    },
    onSuccess: () => invalidate.topology(factoryId),
  });
}

export function useRenameZone(factoryId: string, zoneId: string) {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: async (input: { new_zone_name: string }) => {
      try {
        await factoriesControllerRenameZone(factoryId, zoneId, input);
      } catch (err) {
        mapConflict(err);
      }
    },
    onSuccess: () => invalidate.topology(factoryId),
  });
}

export function useSetZoneArchived(factoryId: string, zoneId: string) {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: async (archived: boolean) => {
      if (archived) {
        await factoriesControllerArchiveZone(factoryId, zoneId);
      } else {
        await factoriesControllerUnarchiveZone(factoryId, zoneId);
      }
    },
    onSuccess: () => invalidate.topology(factoryId),
  });
}

export function useCreateStation(factoryId: string, zoneId: string) {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: async (input: { station_name: string }) => {
      try {
        const res = await factoriesControllerCreateStation(factoryId, zoneId, input);
        return expectData<StationNode>(res);
      } catch (err) {
        mapConflict(err);
      }
    },
    onSuccess: () => invalidate.topology(factoryId),
  });
}

export function useUpdateStation(factoryId: string, zoneId: string, stationId: string) {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: async (input: { new_station_name?: string; new_zone_id?: string }) => {
      try {
        await factoriesControllerUpdateStation(factoryId, zoneId, stationId, input);
      } catch (err) {
        mapConflict(err);
      }
    },
    onSuccess: () => invalidate.topology(factoryId),
  });
}

export function useSetStationArchived(factoryId: string, zoneId: string, stationId: string) {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: async (archived: boolean) => {
      if (archived) {
        await factoriesControllerArchiveStation(factoryId, zoneId, stationId);
      } else {
        await factoriesControllerUnarchiveStation(factoryId, zoneId, stationId);
      }
    },
    onSuccess: () => invalidate.topology(factoryId),
  });
}

export function useDownloadStationScanLabel(factoryId: string, zoneId: string, stationId: string) {
  return useMutation({
    mutationFn: () =>
      fetchBlob(getFactoriesControllerDownloadStationScanLabelUrl(factoryId, zoneId, stationId)),
  });
}

// ── Reorder ────────────────────────────────────────────────────────────────────
//
// The endpoints take the COMPLETE ordered id array for the parent and renumber
// it 1..N, so the caller must never send a filtered subset — the view disables
// dragging whenever the rendered list is not the full set.
//
// Both mutations write the new order into the topology cache optimistically so
// the row doesn't visibly snap back while the request is in flight. Note that
// use-factory-admin-channel.ts blanket-invalidates [FACTORY_ADMIN_KEY] on every
// realtime broadcast, and one reorder emits a notify per moved row, so this
// optimistic state can be replaced mid-flight — harmless, because what replaces
// it is the same order from the server.

/**
 * Fold a reordering of the *visible* rows back into the complete list the
 * endpoints require.
 *
 * Archived rows are hidden by default and are a permanent property of the data
 * — a factory with 30 of its 48 stations archived is ordinary — so reordering
 * cannot demand that everything be on screen. The visible rows are permuted
 * among the slots they already occupy and every hidden row keeps its exact
 * position, which makes the result well-defined without the user ever seeing
 * the rows they are not reordering.
 */
export function reorderVisibleWithinFull<T>(
  full: readonly T[],
  visibleOrderedIds: readonly string[],
  getId: (item: T) => string,
): string[] {
  const ids = full.map(getId);
  const visible = new Set(visibleOrderedIds);
  const slots = ids.reduce<number[]>((acc, id, i) => {
    if (visible.has(id)) acc.push(i);
    return acc;
  }, []);
  // A visible id that isn't in `full` would silently drop a row; leave the
  // order alone rather than submit a set the server will reject.
  if (slots.length !== visibleOrderedIds.length) return ids;
  slots.forEach((slot, k) => {
    ids[slot] = visibleOrderedIds[k]!;
  });
  return ids;
}

/**
 * Rebuild `zones` in `orderedIds` order, restamping zone_sequence to 1..N so the
 * optimistic cache matches what the server will return. Unknown ids are ignored
 * and any zone missing from `orderedIds` keeps its place at the end, so a stale
 * id list can never drop a row from the view.
 */
export function applyZoneOrder(
  zones: readonly ZoneNode[],
  orderedIds: readonly string[],
): ZoneNode[] {
  return orderByIds(zones, orderedIds, (z) => z.zone_id).map((zone, i) => ({
    ...zone,
    zone_sequence: i + 1,
  }));
}

/** Station counterpart of applyZoneOrder. */
export function applyStationOrder(
  stations: readonly StationNode[],
  orderedIds: readonly string[],
): StationNode[] {
  return orderByIds(stations, orderedIds, (s) => s.station_id).map((station, i) => ({
    ...station,
    station_sequence: i + 1,
  }));
}

function orderByIds<T>(
  items: readonly T[],
  orderedIds: readonly string[],
  getId: (item: T) => string,
): T[] {
  const byId = new Map(items.map((item) => [getId(item), item]));
  const ordered = orderedIds
    .map((id) => byId.get(id))
    .filter((item): item is T => item !== undefined);
  const placed = new Set(ordered.map(getId));
  return [...ordered, ...items.filter((item) => !placed.has(getId(item)))];
}

export function useReorderZones(factoryId: string) {
  const queryClient = useQueryClient();
  const invalidate = useInvalidate();
  const key = [FACTORY_ADMIN_KEY, 'topology', factoryId] as const;

  return useMutation({
    mutationFn: (zoneIds: string[]) =>
      factoriesControllerReorderZones(factoryId, { zone_ids: zoneIds }),
    onMutate: async (zoneIds) => {
      await queryClient.cancelQueries({ queryKey: key });
      const previous = queryClient.getQueryData<FactoryTopology>(key);
      if (previous) {
        queryClient.setQueryData<FactoryTopology>(key, {
          ...previous,
          zones: applyZoneOrder(previous.zones, zoneIds),
        });
      }
      return { previous };
    },
    onError: (_err, _zoneIds, context) => {
      if (context?.previous) queryClient.setQueryData(key, context.previous);
    },
    onSettled: () => invalidate.topology(factoryId),
  });
}

export function useReorderStations(factoryId: string, zoneId: string) {
  const queryClient = useQueryClient();
  const invalidate = useInvalidate();
  const key = [FACTORY_ADMIN_KEY, 'topology', factoryId] as const;

  return useMutation({
    mutationFn: (stationIds: string[]) =>
      factoriesControllerReorderStations(factoryId, zoneId, { station_ids: stationIds }),
    onMutate: async (stationIds) => {
      await queryClient.cancelQueries({ queryKey: key });
      const previous = queryClient.getQueryData<FactoryTopology>(key);
      if (previous) {
        queryClient.setQueryData<FactoryTopology>(key, {
          ...previous,
          zones: previous.zones.map((zone) =>
            zone.zone_id === zoneId
              ? { ...zone, stations: applyStationOrder(zone.stations, stationIds) }
              : zone,
          ),
        });
      }
      return { previous };
    },
    onError: (_err, _stationIds, context) => {
      if (context?.previous) queryClient.setQueryData(key, context.previous);
    },
    onSettled: () => invalidate.topology(factoryId),
  });
}
